import React, { useEffect, useContext } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Card,
  CardContent,
  InputLabel,
  IconButton,
  Modal,
  Backdrop,
  CircularProgress,
  Stack,
  Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import Clear from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import { Theme } from "@mui/material/styles";
import { createStyles, makeStyles } from "@mui/styles";
import { GetSeasonality } from "../utils/GetCustomer";
import { EditSeasonality, DeleteSeasonality, AddSeasonality } from "../utils/PostCustomer";
import { useLocalStorage } from "../componentUtils/LocalStorage";
import { AddBox, AddBoxOutlined } from "@mui/icons-material";
import { DisplayRoleContext } from "src/Context_api";

const seasonalityMonths: string[] = [
  "JANUARY",
  "FEBRUARY",
  "MARCH",
  "APRIL",
  "MAY",
  "JUNE",
  "JULY",
  "AUGUST",
  "SEPTEMBER",
  "OCTOBER",
  "NOVEMBER",
  "DECEMBER",
];

interface SeasonalityInfoProps {
  customerNum: string;
}

interface SeasonalityState {
  isLoaded: boolean;
  seasonalityInfo: any;
  isDisabled: boolean;
  selectedRowIndex: number;
}

interface alertFields {
  alertTitle: any;
  alertMessage: any;
  isAlertOpen: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    centerDiv: {
      textAlign: "center",
    },
    root: {},
    divWidth: {
      width: "100%",
    },
    modal: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    modalGrid: {
      marginTop: theme.spacing(2),
    },
    modalTitle: {
      color: theme.palette.primary.main,
    },
    modalCard: {},
    modalText: {
      width: 800,
    },
    modalInput: {
      paddingLeft: "2px",
      paddingRight: "20px",
      width: "280px",
    },
    paper: {
      position: "absolute",
      backgroundColor: theme.palette.background.paper,
      border: `1px solid`,
      borderColor: theme.palette.border.main,
      boxShadow: theme.shadows[4],
      padding: theme.spacing(2, 2, 2)
    },
    gridSpaceable: {
      padding: theme.spacing(2),
    },
    gridMarginTop: {
      marginTop: theme.spacing(2),
    },
    secMarginTop: {
      marginTop: theme.spacing(5),
    },
    tableRow: {
      "&$hover:hover": {
        backgroundColor: "gray",
      },
    },
    hover: {},
  })
);

function getModalStyle() {
  const top = 50;
  const left = 50;

  return {
    top: `${top}%`,
    left: `${left}%`,
    transform: `translate(-${top}%, -${left}%)`,
  };
}

/**
 * Seasonality functionality on customer page
 * @param props
 * @returns
 */
export default function SeasonalityInfo(props: SeasonalityInfoProps) {
  const [modalStyle] = React.useState(getModalStyle);
  const classes = useStyles();
  const {isDisplayOnlyRole}:any = useContext(DisplayRoleContext)
  const [state, setState] = React.useState<SeasonalityState>({
    isLoaded: false,
    seasonalityInfo: [],
    isDisabled: true,
    selectedRowIndex: 0,
  });
  const [currentData, setCurrent] = React.useState<any>({});
  const [displayOption, setDisplayOption] = React.useState<any>({});
  const [salesOrg] = useLocalStorage("SETTINGS.salesOrg", "");
  const [openEditWindow, setOpenEditWindow] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState<boolean>(false);
  const [backdropLoader, setBackdropLoader] = React.useState<boolean>(false);
  const [commonAlert, setCommonAlert] = React.useState<alertFields>({
    alertTitle: "",
    alertMessage: "",
    isAlertOpen: false,
  });
  const [isAdd, setIsAdd] = React.useState<boolean>(false);
  const [showAddButton, setShowAddButton] = React.useState<boolean>(false);

  const initialCurrentData = {
    trade_channel: "",
    subtrade_channel: "",
    customer_num: "",
    jan: "0",
    feb: "0",
    mar: "0",
    apr: "0",
    may: "0",
    jun: "0",
    jul: "0",
    aug: "0",
    sep: "0",
    oct: "0",
    nov: "0",
    dec: "0",
  };

  const updateData = (v: any) => {
    let selectedRowIndex = 0;
    let isCustomerDataPresent = false;
    for (let i = 0; i < v.length; i++) {
      if (v[i].customer_num !== "") {
        selectedRowIndex = i;
        isCustomerDataPresent = true;
        break;
      }
      if (v[i].subtrade_channel !== "") {
        selectedRowIndex = i;
      }
      if (selectedRowIndex === 0 && v[i].trade_channel !== "") {
        selectedRowIndex = i;
      }
    }
    setState({
      ...state,
      isLoaded: true,
      seasonalityInfo: v || [],
      selectedRowIndex: selectedRowIndex,
    });
    setShowAddButton(!isCustomerDataPresent);
  };

  useEffect(() => {
    if (state.isLoaded) return;

    GetSeasonality(salesOrg.toString(), props.customerNum).then((v: any) => updateData(v));
  }, [state]);

  const setCurrentData = (tableMeta: any) => {
    let displayObject: any = {};
    if (tableMeta.customer_num) {
      displayObject.displayLabel = "Customer Number";
      displayObject.displayColumn = "customer_num";
      displayObject.displayValue = tableMeta.customer_num;
    }
    seasonalityMonths.map((key: any) => {
      tableMeta[key.toLowerCase().substring(0, 3)] = tableMeta[key.toLowerCase().substring(0, 3)].toString();
    })
    setDisplayOption({ ...displayObject });
    setCurrent({ ...tableMeta, typeOf: displayObject.displayColumn });
  };

  const handleFactorsOnChange = (value: any, key: string) => {
    const seasonVal = value && value != "-" ? parseInt(value) || 0 : value;
    if (seasonVal < -100 || seasonVal > 100) {
      return setCommonAlert({
        alertTitle: "!Validation Error",
        alertMessage: "Value must be between -100 to 100",
        isAlertOpen: true,
      });
    }
    currentData[key] = seasonVal.toString();
    setCurrent({ ...currentData });
  };

  const handleEdit = (tableMeta: any) => {
    if (tableMeta) setCurrentData(tableMeta);

    setIsAdd(false);
    setOpenEditWindow(true);
  };

  const handleDelete = () => {
    setBackdropLoader(true);
    DeleteSeasonality(salesOrg, displayOption?.displayColumn, displayOption?.displayValue)
      .then((v: any) => {
        setCurrent(initialCurrentData);
        GetSeasonality(salesOrg.toString(), props.customerNum).then((v: any) => {
          setBackdropLoader(false);
          updateData(v);
        });
      })
      .catch((error: any) => {
        setCommonAlert({
          alertTitle: "!Technical Error",
          alertMessage: "Failed to delete the record. ",
          isAlertOpen: true,
        });
        setBackdropLoader(false);
      });
  };

  const handleModalSave = () => {
    if (isAdd) {
      currentData["sept"] = currentData["sep"];
      AddSeasonality(salesOrg, currentData).then((res: any) => {
        if (res) {
          setOpenEditWindow(false);
          GetSeasonality(salesOrg.toString(), props.customerNum).then((v: any) => {
            setBackdropLoader(false);
            updateData(v);
          });
        }
      });
    } else {
      EditSeasonality(salesOrg, props.customerNum, currentData).then((res) => {
        if (res) {
          setOpenEditWindow(false);
          GetSeasonality(salesOrg.toString(), props.customerNum).then((v: any) => {
            setBackdropLoader(false);
            updateData(v);
          });
        }
      });
    }
  };

  const handleModalClose = () => {
    setOpenEditWindow(false);
    setCurrent(initialCurrentData);
  };

  const handleAddSeasonality = () => {
    let displayObject: any = {};
    displayObject.displayLabel = "Customer Number";
    displayObject.displayColumn = "customer_num";
    displayObject.displayValue = props.customerNum;
    initialCurrentData.customer_num = props.customerNum || "";
    setDisplayOption({ ...displayObject });
    setIsAdd(true);
    setCurrent(initialCurrentData);
    setOpenEditWindow(true);
  };

  const modalBody = (
    <div style={modalStyle} className={classes.paper}>
      <Typography variant="h4" className={classes.modalTitle} paragraph>
        {isAdd ? "Add Seasonality" : "Edit Seasonality"}
      </Typography>
      <Grid container spacing={2} className={classes.modalGrid}>
        <Grid item xs={14} md={14} xl={12}>
          <Card className={classes.modalCard}>
            <CardContent>
              <Grid container rowSpacing={2} className={classes.modalGrid}>
                <Grid item xs={12} md={12} xl={10}>
                  <InputLabel>{displayOption?.displayLabel}</InputLabel>
                  <TextField
                    placeholder={displayOption?.displayLabel}
                    value={displayOption?.displayValue}
                    className={classes.modalInput}
                    disabled={true}
                  ></TextField>
                </Grid>
                {seasonalityMonths.map((ele: any) => {
                  return (
                    <Grid item xs={6} md={6} xl={4}>
                      <InputLabel>{ele}</InputLabel>
                      <TextField
                        helperText={"Enter value from -100 to 100"}
                        placeholder="Enter Seasonality Factor"
                        type="number"
                        className={classes.modalInput}
                        value={currentData[ele.toLowerCase().substring(0, 3)]}
                        onChange={(e) => handleFactorsOnChange(e.target.value, ele.toLowerCase().substring(0, 3))}
                      ></TextField>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
          <div style={{ paddingRight: "5px", textAlign: "right" }}>
            <React.Fragment>
              <Tooltip title={"Save"}>
                <IconButton onClick={handleModalSave} color="primary">
                  <SaveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={"Cancel"}>
                <IconButton onClick={handleModalClose} color="primary">
                  <Clear />
                </IconButton>
              </Tooltip>
            </React.Fragment>
          </div>
        </Grid>
      </Grid>
    </div>
  );

  return (
    <div className={classes.root}>
      {showAddButton ? (
        <IconButton
          aria-label="Add Seasonality"
          size="small"
          disabled={isDisplayOnlyRole}
          color="primary"
          onClick={() => {
            handleAddSeasonality();
          }}
        >
          <AddBox /> ADD SEASONALITY
        </IconButton>
      ) : (
        <></>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="right">Trade Channel</TableCell>
              <TableCell align="right">Sub-Trade Channel</TableCell>
              <TableCell align="right">Customer Number</TableCell>
              <TableCell align="right">Customer Name</TableCell>
              <TableCell align="right">Customer Address</TableCell>
              <TableCell align="right">Equipment Number</TableCell>
              <TableCell align="right">Equipment Location</TableCell>
              <TableCell align="right">JANUARY</TableCell>
              <TableCell align="right">FEBRAUARY</TableCell>
              <TableCell align="right">MARCH</TableCell>
              <TableCell align="right">APRIL</TableCell>
              <TableCell align="right">MAY</TableCell>
              <TableCell align="right">JUNE</TableCell>
              <TableCell align="right">JULY</TableCell>
              <TableCell align="right">AUGUST</TableCell>
              <TableCell align="right">SEPTEMBER</TableCell>
              <TableCell align="right">OCTOBER</TableCell>
              <TableCell align="right">NOVEMBER</TableCell>
              <TableCell align="right">DECEMBER</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.seasonalityInfo && state.seasonalityInfo.length > 0 ? (
              state.seasonalityInfo.map((ele: any, index: number) => {
                return (
                  <TableRow
                    key={index}
                    hover
                    classes={{ hover: classes.hover }}
                    className={classes.tableRow}
                    selected={state.selectedRowIndex === index}
                  >
                    <TableCell align="right">
                      {`${
                        ele.trade_channel && ele.trade_channel_name
                          ? ele.trade_channel_name + "(" + ele.trade_channel + ")"
                          : ele.trade_channel || ""
                      }`}
                    </TableCell>
                    <TableCell align="right">
                      {`${
                        ele.subtrade_channel && ele.subtrade_channel_name
                          ? ele.subtrade_channel_name + "(" + ele.subtrade_channel + ")"
                          : ele.subtrade_channel_name || ""
                      }`}
                    </TableCell>
                    <TableCell align="right">{`${ele.customer_num || ""}`}</TableCell>
                    <TableCell align="right">{`${ele.customer_name || ""}`}</TableCell>
                    <TableCell align="right">{`${ele.customer_address || ""}`}</TableCell>
                    <TableCell align="right">{`${ele.equipment_num || ""}`}</TableCell>
                    <TableCell align="right">{`${ele.equipment_location || ""}`}</TableCell>
                    {seasonalityMonths.map((key: any) => {
                      return <TableCell align="right">{ele[key.toLowerCase().substring(0, 3)]}</TableCell>;
                    })}
                    <TableCell align="right">
                      {ele.customer_num ? (
                        <>
                          <Tooltip title={"Edit"}>
                            <IconButton
                                aria-label="edit"
                                color="primary"
                                disabled={isDisplayOnlyRole}
                                onClick={() => {
                                handleEdit(ele);
                              }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                          </Tooltip>
                          <Tooltip title={"Delete"}>
                            <IconButton
                                aria-label="delete"
                                color="primary"
                                disabled={isDisplayOnlyRole}
                                onClick={() => {
                                setCurrentData(ele);
                                setConfirmDelete(true);
                              }}
                              >
                                <DeleteIcon
                              fontSize="small"
                            />
                              </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <></>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={20}>
                  {" "}
                  <Stack sx={{ width: "100%" }} spacing={2}>
                    <Alert severity="info">No Data!</Alert>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Modal open={openEditWindow} onClose={handleModalClose}>
        {modalBody}
      </Modal>
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you wish to delete ?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              handleDelete();
              setConfirmDelete(false);
            }}
            color="primary"
          >
            Yes
          </Button>
          <Button
            onClick={() => {
              setConfirmDelete(false);
            }}
            color="primary"
            autoFocus
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={commonAlert.isAlertOpen} onClose={() => setCommonAlert({ ...commonAlert, isAlertOpen: false })}>
        <DialogTitle>{commonAlert.alertTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <div style={{ whiteSpace: "pre-line" }}>{commonAlert.alertMessage}</div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCommonAlert({ ...commonAlert, isAlertOpen: false });
            }}
            color="primary"
            autoFocus
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
      {/* backdrop loader */}
      <Backdrop sx={{ color: "#fff", zIndex: (theme: any) => theme.zIndex.drawer + 1 }} open={backdropLoader}>
        <CircularProgress color="primary" />
      </Backdrop>
    </div>
  );
}
